<script setup>
import { useApiStore } from '@/js/stores/apiStore';
import { useAuthStore } from '@/js/stores/authStore';
import { usePopupStore } from '@/js/stores/popupStore';
import CartModal from '@/js/views/public/shop/cart/partials/Modal.vue';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const props = defineProps({
    item: {
        type: Object,
        required: true,
    },
    selectedSize: {
        type: String,
        required: false,
    },
    type: {
        type: String,
        required: true,
    },
    color: {
        type: String,
        required: false,
        default: 'red',
    },
});

const route = useRoute();
const authStore = useAuthStore();
const askOfferModal = ref(false);
const emit = defineEmits(['updateSize', 'updateStockLocation']);
const childRef = ref(null);

const apiStore = useApiStore();
const popupStore = usePopupStore();
const { t } = useI18n();

const input = ref({
    firstname: '',
    lastname: '',
    email: '',
    message: '',
    stock_location: 1,
});

const addToCart = async (type, item, size) => {
    if (!size) {
        popupStore.show('warning', 'Veuillez selectionner une taille.');
    } else {
        childRef.value.addToCart(type, item, size);
    }
};

const sendOffer = () => {
    input.value.message =
        'Je recevoir une offre leasing B2B pour le modèle suivant : ' +
        props.item.brand +
        ' ' +
        props.item.model +
        ' - ID - ' +
        props.item.id +
        ' - Taille ' +
        props.selectedSize;
    apiStore
        .apiPost('/api/emails/contact', input.value)
        .then(() => {
            popupStore.show(
                'success',
                t('public.shop.home.reprise.recovery.sent'),
            );
            input.value.firstname = '';
            input.value.lastname = '';
            input.value.email = '';
        })
        .catch(() => {});
};

const sendMailOfferCafeteria = () => {
    input.value.firstname = authStore.$state.user.firstname;
    input.value.lastname = authStore.$state.user.lastname;
    input.value.email = authStore.$state.user.email;
    input.value.companyName = authStore.$state.user.company.name;
    input.value.phone = authStore.$state.user.phone;

    input.value.message =
        'Un client souhaite recevoir une offre pour le modèle suivant : ' +
        props.item.brand +
        ' ' +
        props.item.model +
        ' - ID - ' +
        props.item.id +
        ' - Taille ' +
        props.selectedSize +
        ' - Cette demande a été faite depuis l\'écran "plan cafétéria".';
    apiStore
        .apiPost('/api/emails/contact', input.value)
        .then(() => {
            popupStore.show(
                'success',
                t('public.shop.home.reprise.recovery.sent'),
            );
            askOfferModal.value = false;
        })
        .catch(() => {});
};

const sendEmail = () => {
    input.value.message =
        'Je souhaite être informé lorsque cet article sera disponible : ' +
        props.item.brand +
        ' ' +
        props.item.model +
        ' - ID - ' +
        props.item.id +
        ' - Taille ' +
        props.selectedSize;
    apiStore
        .apiPost('/api/emails/contact', input.value)
        .then(() => {
            popupStore.show(
                'success',
                t('public.shop.home.reprise.recovery.sent'),
            );
            input.value.firstname = '';
            input.value.lastname = '';
            input.value.email = '';
        })
        .catch(() => {});
};
</script>

<template>
    <p class="font-bold text-left text-lg">
        {{ t('public.shop.home.bikes.card.filters.stock_location') }} :<br />
    </p>
    <div class="justify-start">
        <AppSelect
            :options="[
                {
                    label: 'Liège',
                    value: 1,
                },
                {
                    label: 'Bruxelles',
                    value: 2,
                },
            ]"
            optionLabel="label"
            optionValue="value"
            v-model="input.stock_location"
            class="mt-2 text-left w-full"
            style="text-align: left"
            @change="emit('updateStockLocation', input.stock_location)"
            :placeholder="
                $translate('public.shop.home.bikes.card.filters.stock_location')
            "
        />
    </div>
    <p class="font-bold text-left text-lg">
        {{ $translate('public.shop.home.bikes.card.details.size') }}
    </p>
    <div class="flex flex-row gap-2 mt-2 text-lg">
        <div
            v-for="size in item?.sizes"
            :key="size"
            class="flex items-center gap-2 rounded-lg border-1 p-1"
            :class="
                item?.stocks &&
                item?.stocks[size] == false &&
                item?.availability[size] == false &&
                size == selectedSize
                    ? 'bg-red'
                    : item?.stocks &&
                      item?.stocks[size] == false &&
                      item?.availability[size] == true &&
                      size == selectedSize
                    ? 'bg-orange-500'
                    : item?.availability[size] == true &&
                      item?.stocks[size] == false
                    ? 'border-orange-500'
                    : item?.stocks[size] == false
                    ? 'bg-gray border-1 border-red'
                    : size == selectedSize
                    ? 'bg-green font-bold'
                    : 'border-1 border-green'
            "
            @click="emit('updateSize', size)"
        >
            <div
                v-if="item?.stocks[size] == false"
                :class="size == selectedSize ? 'text-white' : 'text-black'"
            >
                <i class="fa-solid fa-xmark"></i>
            </div>
            <div v-else-if="selectedSize == size">
                <i class="fa-solid fa-check text-white font-bold"></i>
            </div>
            <div v-else>
                <i class="fa-regular fa-circle"></i>
            </div>
            <p
                class="text-sm font-bold"
                :class="size == selectedSize ? 'text-white' : 'text-black'"
            >
                {{ size }}
            </p>
        </div>
    </div>
    <p
        v-show="selectedSize"
        class="mt-4 text-lg text-left"
        :style="{
            color: item?.stocks && item?.stocks[selectedSize] ? 'green' : 'red',
        }"
    >
        {{
            item?.stocks && item?.stocks[selectedSize]
                ? $translate('public.shop.home.bikes.card.details.available')
                : item?.availability && item?.availability[selectedSize]
                ? $translate(
                      'public.shop.home.bikes.card.details.soon-available',
                  )
                : $translate(
                      'public.shop.home.bikes.card.details.not-available',
                  )
        }}
    </p>
    <div class="flex flex-row gap-2">
        <primary-button
            class="mt-2 space-x-2 p-2 font-bold"
            v-if="type == 'bike'"
            :color="color"
            @click="addToCart(type, item, selectedSize)"
            :disabled="
                !selectedSize ||
                (!item?.stocks[selectedSize] &&
                    !item?.availability[selectedSize])
            "
        >
            <i class="fas fa-shopping-cart"></i>
            <p>
                {{ $translate('public.shop.home.bikes.card.details.order') }}
            </p>
        </primary-button>
        <primary-button
            class="mt-2 space-x-2 p-2 font-bold"
            v-if="type == 'bike_B2B'"
            :color="color"
            @click="askOfferModal = true"
            :disabled="
                !selectedSize ||
                (!item?.stocks[selectedSize] &&
                    !item?.availability[selectedSize])
            "
        >
            <i class="fas fa-shopping-cart"></i>
            <p>
                {{
                    $translate(
                        'public.shop.home.bikes.card.details.offer.title',
                    )
                }}
            </p>
        </primary-button>
        <primary-button
            v-if="type == 'bike' || type == 'bike_B2B'"
            :color="color"
            @click="
                $router.push({
                    name: 'contact',
                    query: {
                        bikes_catalog_id: route.params.id,
                        category: route.params.category,
                    },
                })
            "
            class="mt-2 space-x-2 p-2 font-bold"
            :disabled="!selectedSize || !item?.stocks[selectedSize]"
        >
            <i class="fa-solid fa-person-biking"></i>
            <p>
                {{ $translate('public.shop.home.bikes.card.details.test') }}
            </p>
        </primary-button>
        <primary-button
            v-if="type == 'bike_cafeteria'"
            :color="color"
            @click="sendMailOfferCafeteria()"
            class="mt-2 space-x-2 p-2 font-bold"
            :disabled="
                (!selectedSize || !item?.stocks[selectedSize]) &&
                type != 'bike_cafeteria'
            "
        >
            <i class="fa-solid fa-person-biking"></i>
            <p>
                {{
                    $translate(
                        'public.shop.home.bikes.card.details.offer.title',
                    )
                }}
            </p>
        </primary-button>
    </div>

    <div
        v-if="
            selectedSize &&
            item?.stocks &&
            !item?.stocks[selectedSize] &&
            type != 'bike_cafeteria'
        "
        class="mt-4"
    >
        <div class="flex flex-row gap-2 text-center">
            <div class="flex flex-col gap-2">
                <label for="firstname">{{
                    $translate('public.contact.form.first-name')
                }}</label>
                <input
                    v-model="input.firstname"
                    :placeholder="$translate('public.contact.form.first-name')"
                    class="max-w-72"
                    :class="{
                        inputIsInvalid: apiStore.$state.errors.firstname,
                    }"
                />
            </div>
            <div class="flex flex-col gap-2">
                <label for="lastname">{{
                    $translate('public.contact.form.last-name')
                }}</label>
                <input
                    v-model="input.lastname"
                    :placeholder="$translate('public.contact.form.last-name')"
                    class="max-w-72"
                    :class="{
                        inputIsInvalid: apiStore.$state.errors.lastname,
                    }"
                />
            </div>

            <div class="flex flex-col gap-2">
                <label for="email">{{
                    $translate('public.contact.form.email')
                }}</label>
                <input
                    v-model="input.email"
                    placeholder="Email"
                    class="max-w-72"
                    :class="{
                        inputIsInvalid: apiStore.$state.errors.email,
                    }"
                />
            </div>
        </div>
        <primary-button color="red" @click="sendEmail">
            {{ $translate('public.shop.home.bikes.card.details.warn-me') }}
        </primary-button>
    </div>

    <div v-if="item?.codes && item?.codes[selectedSize]?.ean_code">
        EAN :
        <strong>{{ item?.codes[selectedSize]?.ean_code }}</strong>
    </div>
    <CartModal ref="childRef" />
    <modal :open="askOfferModal" @close="askOfferModal = false">
        <template #title>
            {{ $translate('public.shop.home.bikes.card.details.offer.title') }}
        </template>
        <template #content>
            <p class="text-left">
                {{
                    $translate('public.shop.home.bikes.card.details.offer.text')
                }}
            </p>
            <form @submit.prevent="sendOffer" class="mt-4">
                <div class="flex flex-row gap-2">
                    <div class="flex flex-col gap-2">
                        <label for="firstname">{{
                            $translate('public.contact.form.first-name')
                        }}</label>

                        <input
                            v-model="input.firstname"
                            required
                            :placeholder="
                                $translate(
                                    'public.shop.home.bikes.card.details.offer.form.firstname',
                                )
                            "
                            :class="{
                                inputIsInvalid:
                                    apiStore.$state.errors.firstname,
                            }"
                        />
                    </div>
                    <div class="flex flex-col gap-2">
                        <label for="lastname">{{
                            $translate(
                                'public.shop.home.bikes.card.details.offer.form.lastname',
                            )
                        }}</label>
                        <input
                            v-model="input.lastname"
                            required
                            :placeholder="
                                $translate(
                                    'public.shop.home.bikes.card.details.offer.form.lastname',
                                )
                            "
                            :class="{
                                inputIsInvalid: apiStore.$state.errors.lastname,
                            }"
                        />
                    </div>
                    <div class="flex flex-col gap-2">
                        <label for="email">{{
                            $translate(
                                'public.shop.home.bikes.card.details.offer.form.email',
                            )
                        }}</label>
                        <input
                            v-model="input.email"
                            required
                            :placeholder="
                                $translate(
                                    'public.shop.home.bikes.card.details.offer.form.email',
                                )
                            "
                            :class="{
                                inputIsInvalid: apiStore.$state.errors.email,
                            }"
                        />
                    </div>
                    <div class="flex flex-col gap-2">
                        <label for="phone">{{
                            $translate(
                                'public.shop.home.bikes.card.details.offer.form.phone',
                            )
                        }}</label>
                        <input
                            v-model="input.phone"
                            required
                            :placeholder="
                                $translate(
                                    'public.shop.home.bikes.card.details.offer.form.phone',
                                )
                            "
                            :class="{
                                inputIsInvalid: apiStore.$state.errors.phone,
                            }"
                        />
                    </div>
                </div>
                <primary-button color="green" @click="sendOffer">
                    {{
                        $translate(
                            'public.shop.home.bikes.card.details.offer.button',
                        )
                    }}
                </primary-button>
            </form>
        </template>
    </modal>
</template>
